import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";
import { useTour } from "@/components/GuidedTour";
import { ActivationChecklist } from "@/components/ActivationChecklist";
import { markActivation } from "@/lib/activation";
import { track } from "@/lib/track";
import { scoreLevel } from "@/components/ScoreBadge";
import { PremortemAlerts } from "@/components/PremortemAlerts";
import { QualityCards } from "@/components/QualityCards";
import { AIBriefing } from "@/components/AIBriefing";
import { NLQBar } from "@/components/NLQBar";
import { LiveIntel } from "@/components/LiveIntel";
import { IntelligenceCoverage } from "@/components/IntelligenceCoverage";
import { BoardBriefing } from "@/components/BoardBriefing";
import { TrendingQuestions } from "@/components/TrendingQuestions";
import { RiskPosture } from "@/components/RiskPosture";
import { FinancialExposure } from "@/components/FinancialExposure";
import { Foresight } from "@/components/Foresight";
import { IntroNote } from "@/components/IntroNote";
import { InfoTip } from "@/components/InfoTip";
import { Seo } from "@/components/Seo";
import { PatentFiledBadge } from "@/components/PatentFiledBadge";
import { Sparkline } from "@/components/Sparkline";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldAlert,
  Radar,
  Network,
  ClipboardCheck,
  ArrowRight,
  Activity,
  Zap,
  FileDown,
  Search as SearchIcon,
  Mail,
  AlertTriangle,
  Gauge,
  Banknote,
  Telescope,
  Sparkles,
  Archive,
} from "lucide-react";

const NAV_CARDS = [
  { to: "/risks", icon: ShieldAlert, label: "Enterprise Risk Graph", permission: "risk.read" },
  { to: "/signals", icon: Radar, label: "Live Signals", permission: "signal.read" },
  { to: "/playbooks", icon: ClipboardCheck, label: "Playbooks", permission: "playbook.read" },
  { to: "/remediation", icon: Zap, label: "Auto-Remediation", permission: "remediation.read" },
  { to: "/suppliers", icon: Network, label: "N-Tier Supply Chain", permission: "supplier.read" },
  { to: "/admin", icon: Activity, label: "Audit Log", role: "admin" },
];

const KpiTile = ({ label, value, accent, tip, spark, sparkLabel }) => (
  <div
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className="rounded-md border border-black/5 bg-white p-5"
  >
    <p className="text-xs font-medium uppercase tracking-wide text-ek-ink/65">
      {tip ? <InfoTip label={label} tip={tip} testid={`kpi-tip-${label.toLowerCase().replace(/\s+/g, "-")}`} /> : label}
    </p>
    <p className={`mt-2 font-display text-3xl font-bold ${accent || "text-ek-ink"}`}>
      {value ?? "—"}
    </p>
    {spark && spark.length >= 2 && (
      <div className="mt-2 flex items-end justify-between gap-2">
        <span className="text-[10px] text-ek-ink/50">{sparkLabel || "30-day trend"}</span>
        <Sparkline
          values={spark}
          width={100}
          height={22}
          color="#7737f6"
          ariaLabel={`${label} 30-day trend`}
          testid={`kpi-spark-${label.toLowerCase().replace(/\s+/g, "-")}`}
        />
      </div>
    )}
  </div>
);

const tabTriggerCls =
  "rounded-sm px-5 py-2 text-sm font-medium text-ek-ink/70 data-[state=active]:bg-ek-purple data-[state=active]:text-white data-[state=active]:shadow-sm transition-colors";

export default function Dashboard() {
  const { user } = useAuth();
  const { startTour } = useTour();
  const [summary, setSummary] = useState(null);
  const [doraSeries, setDoraSeries] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => {});
    // DORA readiness sparkline — soft-fail (tile just omits itself if the
    // series is missing, e.g. a fresh tenant with no history yet).
    api
      .get("/dora/readiness-series?days=30")
      .then(({ data }) => setDoraSeries(data))
      .catch(() => {});
  }, []);

  const cards = NAV_CARDS.filter((c) => {
    if (c.role && user?.role !== c.role) return false;
    if (c.permission && !hasPermission(user, c.permission)) return false;
    return true;
  });

  const canExec = hasPermission(user, "dashboard.read");
  const canRisk = hasPermission(user, "risk.read");
  const canPremortem = hasPermission(user, "premortem.read");
  const canDismiss = hasPermission(user, "premortem.dismiss");

  // Land each role on the tab most relevant to them (falling back to Overview
  // when the preferred tab isn't available for their permissions).
  const availableTabs = ["overview", ...(canRisk ? ["financial", "foresight"] : []), "intelligence"];
  const rolePreferredTab = { cfo: "financial", ciso: "intelligence", cpo: "intelligence" }[user?.role];
  const defaultTab = availableTabs.includes(rolePreferredTab) ? rolePreferredTab : "overview";

  // Remember the tab a user last viewed (falls back to their role default).
  const tabStorageKey = `erio_dash_tab_${user?.user_id || user?.email || "anon"}`;
  const [tab, setTab] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(tabStorageKey) : null;
    return availableTabs.includes(saved) ? saved : defaultTab;
  });
  const onTabChange = (v) => {
    setTab(v);
    if (v === "foresight") markActivation("foresight");
    track("dashboard_tab", { tab: v });
    try { localStorage.setItem(tabStorageKey, v); } catch { /* ignore */ }
  };
  useEffect(() => {
    if (tab === "foresight") markActivation("foresight");
  }, [tab]);

  // First-run guided tour is handled globally by GuidedTour (TourProvider).
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipient, setRecipient] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  // iter109 — Compare-to-previous-lodged-pack picker. Pulls the
  // tenant's archive so the user can pick "vs March 2026 board" rather
  // than always comparing against the most recent lodgement.
  const [lodgedPacks, setLodgedPacks] = useState([]);
  const [compareToPackId, setCompareToPackId] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/exports/board-pack/archive")
      .then(({ data }) => { if (!cancelled) setLodgedPacks(data || []); })
      .catch(() => { /* archive may be empty, ignore */ });
    return () => { cancelled = true; };
  }, []);

  const emailBoardPack = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const asOf = new Date().toISOString();
      const { data } = await api.post("/exports/board-pack/email", { as_of: asOf, recipient });
      toast.success(`Board pack emailed to ${data.recipient}`);
      setEmailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email delivery failed");
    } finally {
      setSending(false);
    }
  };

  const downloadBoardPack = async (reportType = "executive") => {
    setExporting(true);
    try {
      const asOf = new Date().toISOString();
      // "all" is a synthetic flavour — single GET to the preview-all
      // bundle endpoint that streams a zip of every flavour for this
      // tenant. Used for visual-polish iterations and pilot demos.
      if (reportType === "all") {
        const res = await api.get(
          "/exports/board-pack/preview-all",
          { params: { as_of: asOf }, responseType: "blob" },
        );
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ERIO-Board-Pack-All-${asOf.slice(0, 10)}.zip`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        toast.success("All three pack flavours exported (zip)");
        return;
      }
      const body = { as_of: asOf, report_type: reportType };
      // Only the executive pack honours the comparator picker; the
      // other types don't have a delta line on the cover.
      if (reportType === "executive" && compareToPackId) {
        body.compare_to_pack_id = compareToPackId;
      }
      const res = await api.post(
        "/exports/board-pack",
        body,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const label =
        reportType === "detailed" ? "Risk-Operating-Review" :
        reportType === "appendix" ? "Reference-Appendix" :
        "Board-Pack-Executive";
      a.download = `ERIO-${label}-${asOf.slice(0, 10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(
        reportType === "detailed" ? "Detailed Risk Operating Review exported" :
        reportType === "appendix" ? "Reference Appendix exported" :
        "Board pack (Executive) exported",
      );
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const heatmap = summary?.heatmap && (
    <div className={canPremortem ? "lg:col-span-2" : "lg:col-span-3"}>
      <h2 className="ek-eyebrow">Risk theme heatmap</h2>
      <p className="mt-1 text-sm text-ek-ink/65">Shaded by the highest residual score per theme.</p>
      <div className="mt-4 grid grid-cols-3 gap-3" data-testid="theme-heatmap">
        {summary.heatmap.map((h) => {
          const level = scoreLevel(h.max_residual || 1);
          const bg =
            h.max_residual === 0
              ? "bg-white border-black/5"
              : level === "critical"
              ? "bg-red-50 border-red-200"
              : level === "high"
              ? "bg-orange-50 border-orange-200"
              : level === "medium"
              ? "bg-amber-50 border-amber-200"
              : "bg-emerald-50 border-emerald-200";
          return (
            <Link
              key={h.theme_id}
              to="/risks"
              data-testid={`heatmap-tile-${h.theme_id}`}
              className={`rounded-md border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${bg}`}
            >
              <p className="text-sm font-semibold text-ek-ink">{h.name}</p>
              <div className="mt-3 flex items-end justify-between">
                <span className="font-display text-3xl font-bold text-ek-ink">{h.max_residual || "—"}</span>
                <span className="text-xs text-ek-ink/65">{h.count} risks</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Dashboard" description="Live enterprise risk posture, KPIs, pre-mortem alerts and board-pack reporting." path="/dashboard" />
      <Navbar />
      <main id="main-content" className="mx-auto max-w-7xl px-6 py-12" data-testid="dashboard">
        <div className="space-y-5">
          <div>
            <p className="ek-eyebrow">Dashboard</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">
              Welcome, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
              Signed in as <span className="font-semibold text-ek-ink">{user?.role?.toUpperCase()}</span>.
              A live view of the ERIO programme.
            </p>
            <div className="mt-3">
              <PatentFiledBadge />
            </div>
          </div>
          <div className="flex flex-wrap items-end justify-start gap-2" data-testid="dashboard-toolbar">
            <button onClick={startTour} data-testid="dashboard-take-tour" className="ek-btn-outline flex items-center gap-1.5 !py-3 text-sm">
              <Sparkles size={16} /> Take the tour
            </button>
            <Link to="/explore" data-testid="dashboard-explore-link" className="ek-btn-outline flex items-center gap-1.5 !py-3 text-sm" title="Search and pivot your tenant's data across risks, suppliers, signals and owners">
              <SearchIcon size={16} /> Explore
            </Link>
            {canExec && (
              <>
                {lodgedPacks.length > 0 && (
                  <div className="flex flex-col" data-testid="compare-to-picker">
                    <label className="text-[10px] font-medium uppercase tracking-wider text-ek-ink/50 mb-0.5">
                      Compare vs
                    </label>
                    <select
                      value={compareToPackId}
                      onChange={(e) => setCompareToPackId(e.target.value)}
                      data-testid="compare-to-select"
                      className="h-[42px] rounded border border-ek-ink/15 bg-white px-3 text-sm text-ek-ink focus:border-ek-purple focus:outline-none"
                      title="Pick a baseline lodged pack to compute the cover delta against (default: most recent)"
                    >
                      <option value="">Most recent (default)</option>
                      {lodgedPacks.map((p) => (
                        <option key={p.pack_id} value={p.pack_id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button onClick={() => setEmailOpen(true)} data-testid="email-board-pack-button" className="ek-btn-outline !py-3 text-sm">
                  <Mail size={16} /> Email
                </button>
                <div className="inline-flex">
                  <button
                    onClick={() => downloadBoardPack("executive")}
                    disabled={exporting}
                    data-testid="export-board-pack-button"
                    className="ek-btn rounded-r-none disabled:opacity-50 min-w-[200px] justify-center"
                    title="Focused 8–12 page Executive pack for the main board"
                  >
                    <FileDown size={16} /> {exporting ? "Generating…" : "Board pack (PDF)"}
                  </button>
                  <button
                    onClick={() => downloadBoardPack("detailed")}
                    disabled={exporting}
                    data-testid="export-board-pack-detailed-button"
                    className="ek-btn rounded-none border-l border-white/30 px-3"
                    title="Full Risk Operating Review (50–80 pages) for the Audit & Risk Committee"
                    aria-label="Export Detailed Risk Operating Review"
                  >
                    Detailed
                  </button>
                  <button
                    onClick={() => downloadBoardPack("appendix")}
                    disabled={exporting}
                    data-testid="export-board-pack-appendix-button"
                    className="ek-btn rounded-none border-l border-white/30 px-3"
                    title="Static reference (methodology, control library, KRI catalogue, glossary)"
                    aria-label="Export Reference Appendix"
                  >
                    Appendix
                  </button>
                  <button
                    onClick={() => downloadBoardPack("all")}
                    disabled={exporting}
                    data-testid="export-board-pack-all-button"
                    className="ek-btn rounded-l-none border-l border-white/30 px-3"
                    title="Download Executive + Detailed + Appendix as a single zip — handy for visual review cycles"
                    aria-label="Export all three pack flavours as a zip"
                  >
                    All (zip)
                  </button>
                </div>
                <Link
                  to="/pack-archive"
                  data-testid="open-pack-archive-link"
                  className="ek-btn-outline flex items-center gap-1.5 !py-3 text-sm"
                  title="Immutable archive of lodged board pack bundles"
                >
                  <Archive size={16} /> Archive
                </Link>
              </>
            )}
          </div>
        </div>

        <Tabs value={tab} onValueChange={onTabChange} className="mt-8">
          <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-md border border-black/5 bg-white p-1" data-testid="dashboard-tabs">
            <TabsTrigger value="overview" className={tabTriggerCls} data-testid="dashboard-tab-overview">Overview</TabsTrigger>
            {canRisk && <TabsTrigger value="financial" className={tabTriggerCls} data-testid="dashboard-tab-financial">Financial</TabsTrigger>}
            {canRisk && <TabsTrigger value="foresight" className={tabTriggerCls} data-testid="dashboard-tab-foresight">Foresight</TabsTrigger>}
            <TabsTrigger value="intelligence" className={tabTriggerCls} data-testid="dashboard-tab-intelligence">Intelligence</TabsTrigger>
          </TabsList>

          {/* ---------------- Overview ---------------- */}
          <TabsContent value="overview" className="mt-6 focus:outline-none" data-testid="dashboard-panel-overview">
            <ActivationChecklist />
            <IntroNote icon={Gauge} title="Overview — your risk at a glance" testid="overview-intro">
              The single place to read the organisation's live risk posture: one composite index and its trend,
              the headline KPIs, anything now over appetite, the theme heatmap and the risks our pre-mortem
              expects to worsen. Start here every day.
            </IntroNote>
            <BoardBriefing />
            {canRisk && <RiskPosture />}

            {summary?.appetite_breaches?.count > 0 && (
              <Link to="/appetite" data-testid="appetite-breach-banner"
                className="mt-8 flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-5 py-4 transition-colors hover:bg-red-100">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-100 text-red-600">
                    <AlertTriangle size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-red-700">
                      {summary.appetite_breaches.count} risk theme{summary.appetite_breaches.count !== 1 ? "s are" : " is"} over appetite
                    </p>
                    <p className="text-xs text-red-700">
                      {summary.appetite_breaches.breaches.map((b) => b.theme_name).join(" · ")}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-semibold text-red-700">Review appetite →</span>
              </Link>
            )}

            {/* KPI strip */}
            {summary?.kpis ? (
              <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="exec-kpis">
                <KpiTile label="Open Risks" value={summary.kpis.open_risks} accent="text-ek-purple" tip="Risks currently active (not closed) across all themes." />
                <KpiTile label="Critical Signals 30d" value={summary.kpis.critical_signals_30d} accent="text-red-600" tip="High-severity intelligence signals received in the last 30 days." />
                <KpiTile label="Pre-Mortem Alerts" value={summary.kpis.active_premortem_alerts} accent="text-orange-600" tip="Risks our forward projection expects to worsen over the next 30 days." />
                <KpiTile label="Runs In Progress" value={summary.kpis.playbook_runs_in_progress} tip="Playbook responses currently being executed." />
                <KpiTile label="Suppliers At Risk" value={summary.kpis.suppliers_at_risk} accent="text-red-600" tip="Suppliers flagged as elevated exposure in the n-tier graph." />
                {doraSeries?.current?.fast_recover_pct != null && (
                  <KpiTile
                    label="DORA Readiness"
                    value={`${doraSeries.current.fast_recover_pct.toFixed(1)}%`}
                    accent="text-emerald-600"
                    tip="Share of critical £/day exposure covered by an RTO ≤ 4h. Trend from the last 30 daily snapshots."
                    spark={doraSeries.fast_recover_series}
                    sparkLabel="30d fast-recover coverage"
                  />
                )}
              </div>
            ) : (
              <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {summary?.risks && (
                  <>
                    <KpiTile label="Open Risks" value={summary.risks.open} accent="text-ek-purple" />
                    <KpiTile label="High Residual" value={summary.risks.high_residual} accent="text-orange-600" />
                  </>
                )}
                {summary?.signals && (
                  <>
                    <KpiTile label="Critical Signals" value={summary.signals.critical} accent="text-red-600" />
                    <KpiTile label="Total Signals" value={summary.signals.total} />
                  </>
                )}
                {!summary?.risks && !summary?.signals && (
                  <KpiTile label="Your access" value={user?.role?.toUpperCase()} />
                )}
              </div>
            )}

            {/* Theme heatmap + pre-mortem rail */}
            {(summary?.heatmap || canPremortem) && (
              <div className="mt-10 grid gap-8 lg:grid-cols-3">
                {heatmap}
                {canPremortem && (
                  <div className={summary?.heatmap ? "" : "lg:col-span-3"}>
                    <PremortemAlerts canDismiss={canDismiss} />
                  </div>
                )}
              </div>
            )}

            {/* Quality summary cards — drill-down to PIS / RRQS */}
            {canRisk && (
              <>
                <h2 className="ek-eyebrow mt-12">Quality surfaces</h2>
                <div className="mt-4">
                  <QualityCards />
                </div>
              </>
            )}

            {/* Quick links — fast jumps into the live modules */}
            {cards.length > 0 && (
              <>
                <h2 className="ek-eyebrow mt-12">Quick links</h2>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="dashboard-quick-links">
                  {cards.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      data-testid={`dashboard-card-${c.to.slice(1)}`}
                      className="group flex flex-col items-start gap-3 rounded-md border border-black/5 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-ek-purple/20 hover:shadow-md"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-ek-purple/10 text-ek-purple transition-colors group-hover:bg-ek-purple group-hover:text-white">
                        <c.icon size={18} />
                      </span>
                      <span className="flex items-center gap-1 text-sm font-semibold leading-tight text-ek-ink">
                        {c.label}
                        <ArrowRight size={13} className="text-ek-ink/20 transition-all group-hover:translate-x-0.5 group-hover:text-ek-purple" />
                      </span>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* ---------------- Financial ---------------- */}
          {canRisk && (
            <TabsContent value="financial" className="mt-6 focus:outline-none" data-testid="dashboard-panel-financial">
              <IntroNote icon={Banknote} title="Financial — risk in money, not colours" testid="financial-intro">
                Exposure quantified with Open-FAIR Monte-Carlo simulation: the expected annual loss, the
                worst-case (P90), the loss-exceedance curve and the risks contributing most. This is the language
                the board decides in — and the figure you can defend.
              </IntroNote>
              <FinancialExposure />
            </TabsContent>
          )}

          {/* ---------------- Foresight ---------------- */}
          {canRisk && (
            <TabsContent value="foresight" className="mt-6 focus:outline-none" data-testid="dashboard-panel-foresight">
              <IntroNote icon={Telescope} title="Foresight — the decision-intelligence layer" testid="foresight-intro">
                Not just where risk is, but where it is going. Risk velocity and projected appetite breaches,
                a counterfactual replay of what earlier action would have saved in £, and scenario contagion
                that cascades a board-level shock through your supply chain to a quantified £ impact — every
                figure shown as a range with its confidence and assumptions. Decision support, not autopilot.
              </IntroNote>
              <Foresight />
            </TabsContent>
          )}

          {/* ---------------- Intelligence ---------------- */}
          <TabsContent value="intelligence" className="mt-6 space-y-2 focus:outline-none" data-testid="dashboard-panel-intelligence">
            <IntroNote icon={Radar} title="Intelligence — the outside view" testid="intelligence-intro">
              Live external signal feeding your risk picture: what leadership is asking, the sanctions
              Watchtower and GDELT threat feed, an AI executive briefing, and a natural-language bar to ask
              ERIO anything. This is where emerging risk shows up first.
            </IntroNote>
            {canExec && <TrendingQuestions canConfigure={hasPermission(user, "risk.write")} />}
            {hasPermission(user, "supplier.read") && <IntelligenceCoverage />}
            <LiveIntel user={user} />
            {canExec && <AIBriefing />}
          </TabsContent>
        </Tabs>

        {/* Ask ERIO — natural-language bar available on every dashboard tab */}
        <NLQBar />

        {/* Email board pack dialog */}
        <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
          <DialogContent data-testid="email-board-pack-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Email board pack</DialogTitle>
              <DialogDescription>
                Generate the PDF board pack and send it as an attachment via Resend.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={emailBoardPack} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-ek-ink">Recipient email</label>
                <input
                  type="email"
                  required
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  data-testid="email-recipient-input"
                  placeholder="board@example.com"
                  className="w-full rounded-[4px] border border-black/10 bg-white px-3 py-2 text-sm text-ek-ink outline-none transition-colors focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
                />
                <p className="mt-1.5 text-xs text-ek-ink/65">
                  Demo sender is <code className="bg-ek-canvas px-1">onboarding@resend.dev</code> —
                  in Resend test mode, delivery is limited to your verified account address.
                </p>
              </div>
              <DialogFooter>
                <button type="submit" disabled={sending} data-testid="email-board-pack-submit" className="ek-btn w-full disabled:opacity-50">
                  {sending ? "Sending…" : "Send board pack"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
